import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import {
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FieldTimeOutlined,
  PlusOutlined,
  SaveOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import * as settingsService from "../../services/settingsService";
import Loadingpage from "../Loadingpage/Loadingpage";

const formatDurationLabel = (seconds) => {
  if (!seconds) return "";
  if (seconds >= 60 && seconds % 60 === 0) {
    const mins = seconds / 60;
    return mins === 1 ? "1m" : `${mins}m`;
  }
  return `${seconds}s`;
};

const durationUnitOptions = [
  { label: "Seconds", value: "sec" },
  { label: "Minutes", value: "min" },
];

const getTimeFormValues = (record) => {
  if (!record) return {};
  if (record.durationValue && record.durationUnit) {
    return { durationValue: record.durationValue, durationUnit: record.durationUnit };
  }
  if (record.seconds >= 60 && record.seconds % 60 === 0) {
    return { durationValue: record.seconds / 60, durationUnit: "min" };
  }
  return { durationValue: record.seconds, durationUnit: "sec" };
};

const getDurationSeconds = (value, unit) => (unit === "min" ? value * 60 : value);

const getDurationDisplay = (record) => {
  const { durationValue, durationUnit } = getTimeFormValues(record);
  return durationUnit === "min" ? `${durationValue} min` : `${durationValue} sec`;
};

const ManageTimeAmountSettings = () => {
  const [form] = Form.useForm();
  const [timeSettings, setTimeSettings] = useState([]);
  const [amountSettings, setAmountSettings] = useState([]);
  const [activeTab, setActiveTab] = useState("time");
  const [activeRole, setActiveRole] = useState("basic");
  const [modalType, setModalType] = useState("time");
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isEditItemChanged, setIsEditItemChanged] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterLoading, setFilterLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchData = async (role = activeRole) => {
    setFilterLoading(true);
    try {
      const response = await settingsService.getBinarySettings({ role });
      if (response?.success) {
        setTimeSettings(response.result.time_settings || []);
        setAmountSettings(response.result.amount_settings || []);
      } else {
        messageApi.error(`Failed to fetch ${role} settings`);
      }
    } catch (error) {
      messageApi.error(`Failed to fetch ${role} settings`);
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeRole]);

  const getFilteredList = (list) => {
    if (!filterStatus) return list;
    const isActiveFilter = filterStatus === "true";
    return list.filter((item) => item.isActive === isActiveFilter);
  };

  const applyStatusFilter = (value) => {
    setFilterStatus(value || "");
  };

  const clearStatusFilter = () => {
    setFilterStatus("");
  };

  const openAddModal = (type) => {
    setModalType(type);
    setEditingItem(null);
    setIsEditItemChanged(false);
    setIsFormModalOpen(true);
    form.setFieldsValue({
      order: (type === "time" ? timeSettings : amountSettings).length + 1,
      isActive: true,
      durationValue: undefined,
      durationUnit: "sec",
      amount: undefined,
    });
  };

  const openEditModal = (type, record) => {
    setModalType(type);
    setEditingItem(record);
    setIsEditItemChanged(false);
    setIsFormModalOpen(true);
    form.setFieldsValue({
      ...record,
      ...getTimeFormValues(record),
    });
  };

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setEditingItem(null);
    setIsEditItemChanged(false);
    form.resetFields();
  };

  const handleFormValuesChange = () => {
    setIsEditItemChanged(true); // Simplified for restoration
  };

  const handleSave = async (values) => {
    const isTime = modalType === "time";
    const seconds = isTime ? getDurationSeconds(values.durationValue, values.durationUnit) : undefined;
    const normalizedLabel = isTime ? formatDurationLabel(seconds) : `$${values.amount}`;

    const payload = {
      role: activeRole,
      id: editingItem?._id || editingItem?.id,
      order: values.order,
      isActive: values.isActive ?? true,
      label: normalizedLabel,
      ...(isTime ? { seconds, durationValue: values.durationValue, durationUnit: values.durationUnit } : { amount: values.amount }),
    };

    setFilterLoading(true);
    const response = isTime ? await settingsService.saveTimeSetting(payload) : await settingsService.saveAmountSetting(payload);

    if (response?.success) {
      messageApi.success("Setting saved");
      fetchData();
      closeFormModal();
    } else {
      messageApi.error(response?.message || "Failed to save setting");
    }
    setFilterLoading(false);
  };

  const handleDelete = async () => {
    const isTime = deletingItem?.type === "time";
    const id = deletingItem.record._id || deletingItem.record.id;

    setFilterLoading(true);
    const response = isTime ? await settingsService.deleteTimeSetting(id) : await settingsService.deleteAmountSetting(id);

    if (response?.success) {
      messageApi.success("Deleted successfully");
      fetchData();
    } else {
      messageApi.error("Failed to delete");
    }
    setDeletingItem(null);
    setFilterLoading(false);
  };

  const handleStatusChange = async (type, record, checked) => {
    const isTime = type === "time";
    const payload = { role: activeRole, id: record._id || record.id, isActive: checked };
    setFilterLoading(true);
    const response = isTime ? await settingsService.saveTimeSetting(payload) : await settingsService.saveAmountSetting(payload);
    if (response?.success) {
      messageApi.success("Status updated");
      fetchData();
    } else {
      messageApi.error("Failed to update status");
    }
    setFilterLoading(false);
  };

  const columns = (type) => {
    const isTime = type === "time";
    return [
      { title: "Order", dataIndex: "order", key: "order", width: 100 },
      { title: isTime ? "Duration" : "Amount", dataIndex: isTime ? "seconds" : "amount", key: "val", render: (v) => (isTime ? `${v}s` : `$${v}`) },
      { title: "Label", dataIndex: "label", key: "label", render: (_, r) => isTime ? getDurationDisplay(r) : r.label },
      { title: "Status", dataIndex: "isActive", key: "isActive", render: (isActive, record) => <Switch checked={isActive} onChange={(c) => handleStatusChange(type, record, c)} /> },
      { title: "State", dataIndex: "isActive", key: "state", render: (isActive) => isActive ? <Tag color="success">Enabled</Tag> : <Tag color="default">Disabled</Tag> },
      { title: "Actions", key: "actions", width: 130, render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditModal(type, record)} />
          <Button className="time-amount-danger-btn" danger icon={<DeleteOutlined />} onClick={() => setDeletingItem({ type, record })} />
        </Space>
      )},
    ];
  };

  const renderSettingsPanel = (type) => {
    const isTime = type === "time";
    const data = getFilteredList(isTime ? timeSettings : amountSettings);
    return (
      <Card
        className="trade-pattern-table-card"
        title={isTime ? `Time Settings - ${activeRole.toUpperCase()}` : `Amount Settings - ${activeRole.toUpperCase()}`}
        extra={
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Select allowClear placeholder="Status" value={filterStatus || undefined} onChange={applyStatusFilter} style={{ width: 120 }}>
              <Select.Option value="true">Active</Select.Option>
              <Select.Option value="false">Inactive</Select.Option>
            </Select>
            <Button className="time-amount-danger-btn" disabled={!filterStatus} icon={<CloseCircleOutlined />} onClick={clearStatusFilter} danger>Clear</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openAddModal(type)}>Add {isTime ? "Time" : "Amount"}</Button>
          </div>
        }
      >
        <Table
          className="table-clr"
          columns={columns(type)}
          dataSource={data}
          rowKey={(record) => record._id || record.id}
          pagination={false}
          loading={filterLoading}
          scroll={{ x: 800 }}
        />
      </Card>
    );
  };

  return (
    <div className="trade-pattern-page">
      <Loadingpage isLoading={filterLoading} />
      {contextHolder}
      
      <div className="display-2 p-10 trade-pattern-header">
        <div>
          <h2>Manage Time and Amount</h2>
          <p>Control trade durations and amounts per user role.</p>
        </div>
      </div>

      <Card className="trade-pattern-info-card">
        <div className="time-amount-summary-grid">
          <div>
            <h3>Trading Controls</h3>
            <p>Settings for {activeRole.toUpperCase()} users.</p>
          </div>
          <div className="time-amount-stat">
            <FieldTimeOutlined />
            <span>Active Time</span>
            <strong>{timeSettings.filter(i => i.isActive).length}</strong>
          </div>
          <div className="time-amount-stat">
            <DollarOutlined />
            <span>Active Amount</span>
            <strong>{amountSettings.filter(i => i.isActive).length}</strong>
          </div>
        </div>
      </Card>

      <Tabs 
        activeKey={activeRole} 
        onChange={setActiveRole} 
        items={[
          { key: "basic", label: "Basic Role", children: null },
          { key: "vip", label: "VIP Role", children: null },
          { key: "vvip", label: "VVIP Role", children: null },
        ]}
        className="custom-tabs"
      />

      <div style={{ marginTop: "24px" }}>
        <Tabs
          className="time-amount-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: "time", label: <><FieldTimeOutlined /> Time</>, children: renderSettingsPanel("time") },
            { key: "amount", label: <><DollarOutlined /> Amount</>, children: renderSettingsPanel("amount") },
          ]}
        />
      </div>

      <Modal
        title={`${editingItem ? "Edit" : "Add"} ${modalType === "time" ? "Time" : "Amount"}`}
        open={isFormModalOpen}
        onCancel={closeFormModal}
        footer={null}
        maskClosable={false}
        centered
        destroyOnHidden
        className="custom-modal trade-pattern-modal"
      >
        <Form form={form} layout="vertical" onFinish={handleSave} onValuesChange={handleFormValuesChange} initialValues={{ isActive: true, durationUnit: "sec" }}>
          <div className="time-amount-form-grid">
            <Form.Item label="Order" name="order" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
            {modalType === "time" ? (
              <>
                <Form.Item label="Duration" name="durationValue" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
                <Form.Item label="Unit" name="durationUnit" rules={[{ required: true }]}><Select options={durationUnitOptions} /></Form.Item>
              </>
            ) : (
              <Form.Item label="Amount" name="amount" rules={[{ required: true }]}><InputNumber min={0.01} style={{ width: '100%' }} /></Form.Item>
            )}
          </div>
          <Form.Item label="Status" name="isActive" valuePropName="checked"><Switch checkedChildren="Active" unCheckedChildren="Inactive" /></Form.Item>
          <div className="trade-pattern-form-actions">
            <Button onClick={closeFormModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>Save</Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Confirm Delete"
        open={Boolean(deletingItem)}
        onCancel={() => setDeletingItem(null)}
        footer={null}
        centered
      >
        <div className="trade-pattern-delete-modal">
          <p>Are you sure you want to delete <strong>{deletingItem?.record?.label}</strong>?</p>
          <div className="trade-pattern-form-actions">
            <Button onClick={() => setDeletingItem(null)}>Cancel</Button>
            <Button className="time-amount-danger-btn" type="primary" danger icon={<DeleteOutlined />} onClick={handleDelete}>Delete</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ManageTimeAmountSettings;

